import { useState } from 'react';
import { Box, Menu, MenuItem, Typography, styled, useTheme } from '@mui/material';
import SvgIcon from '../SvgIcon';
import { ReactComponent as MoreIcon } from '../../assets/icons/24/more.svg';
import { ReactComponent as RemoveIcon } from '../../assets/icons/32/remove.svg';
import { ReactComponent as BonusIcon } from '../../assets/icons/32/bonus.svg';
import { ReactComponent as AddIcon } from '../../assets/icons/32/add.svg';
import { ReactComponent as ReduceIcon } from '../../assets/icons/32/reduce.svg';
import { getThemeColors } from '../../helpers/themeStyles';

const ActionsCellWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: '100%',
  height: '100%',
});

const MoreButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: 8,
  cursor: 'pointer',
  color: getThemeColors(theme).textSecondary,
  '&:hover': {
    backgroundColor: getThemeColors(theme).surfaceSecondary,
    color: getThemeColors(theme).textPrimary,
  },
}));

const ActionsMenu = styled(Menu)(({ theme }) => {
  const colors = getThemeColors(theme);

  return {
    '& .MuiPaper-root': {
      backgroundColor: colors.menuBg,
      borderRadius: 14,
      marginTop: 4,
      border: `1px solid ${colors.borderSubtle}`,
      minWidth: 220,
      color: colors.menuItemText,
    },
    '& .MuiList-root': {
      padding: 8,
    },
  };
});

const ActionMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== '$danger',
})(({ theme, $danger }) => {
  const colors = getThemeColors(theme);

  return {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 12px',
    borderRadius: 10,
    color: $danger ? '#FF6B6B' : colors.menuItemText,
    '&:hover': {
      backgroundColor: colors.surfaceSecondary,
    },
  };
});

const DriversActionsCell = ({
  data,
  onGiveBonus,
  onAddBalance,
  onReduceBalance,
  onSelectRow,
  onRemove,
}) => {
  const theme = useTheme();
  const colors = getThemeColors(theme);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  if (!data || data.id === 'footer') return null;

  const handleOpen = (e) => {
    e.stopPropagation();
    onSelectRow?.(data);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const runAction = (handler) => (e) => {
    e.stopPropagation();
    handleClose();
    handler?.();
  };

  return (
    <ActionsCellWrapper>
      <MoreButton
        onClick={handleOpen}
        aria-label="Actions"
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <SvgIcon icon={MoreIcon} color="inherit" size={20} />
      </MoreButton>
      <ActionsMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <ActionMenuItem onClick={runAction(onGiveBonus)} disableRipple>
          <SvgIcon icon={BonusIcon} color={colors.iconOnSurface} size={20} />
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Donner un bonus</Typography>
        </ActionMenuItem>
        <ActionMenuItem onClick={runAction(onAddBalance)} disableRipple>
          <SvgIcon icon={AddIcon} color={colors.iconOnSurface} size={20} />
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Ajouter un solde</Typography>
        </ActionMenuItem>
        <ActionMenuItem onClick={runAction(onReduceBalance)} disableRipple>
          <SvgIcon icon={ReduceIcon} color={colors.iconOnSurface} size={20} />
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Réduire le solde</Typography>
        </ActionMenuItem>
        <ActionMenuItem $danger onClick={runAction(() => onRemove?.(data))} disableRipple>
          <SvgIcon icon={RemoveIcon} color="#FF6B6B" size={20} />
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Supprimer le chauffeur</Typography>
        </ActionMenuItem>
      </ActionsMenu>
    </ActionsCellWrapper>
  );
};

export default DriversActionsCell;
